import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useCallback,
  ReactNode,
} from "react";
import type { PeriodValue, PeriodKey } from "../../components/Common/DateRangeFilter";

interface DashboardFilterContextValue {
  period: PeriodValue;
  setPeriod: (v: PeriodValue) => void;
  cinemaId: string | null;
  setCinemaId: (v: string | null) => void;
  resetFilters: () => void;
  isCustom: boolean;
  dateRange: { dateFrom: string; dateTo: string } | undefined;
}

const DEFAULT_PERIOD: PeriodKey = "this_week";
const DEFAULT_CINEMA: string | null = null;

const DashboardFilterContext = createContext<DashboardFilterContextValue | undefined>(
  undefined,
);

export const DashboardFilterProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [period, setPeriodState] = useState<PeriodValue>({
    period: DEFAULT_PERIOD,
  });
  const [cinemaId, setCinemaIdState] = useState<string | null>(DEFAULT_CINEMA);

  const setPeriod = useCallback((v: PeriodValue) => setPeriodState(v), []);
  const setCinemaId = useCallback((v: string | null) => setCinemaIdState(v), []);

  const resetFilters = useCallback(() => {
    setPeriodState({ period: DEFAULT_PERIOD });
    setCinemaIdState(DEFAULT_CINEMA);
  }, []);

  const isCustom = period.period === "custom";
  const dateRange = useMemo(() => {
    if (!isCustom || !period.dateFrom || !period.dateTo) return undefined;
    return { dateFrom: period.dateFrom, dateTo: period.dateTo };
  }, [isCustom, period.dateFrom, period.dateTo]);

  const value = useMemo<DashboardFilterContextValue>(
    () => ({
      period,
      setPeriod,
      cinemaId,
      setCinemaId,
      resetFilters,
      isCustom,
      dateRange,
    }),
    [period, setPeriod, cinemaId, setCinemaId, resetFilters, isCustom, dateRange],
  );

  return (
    <DashboardFilterContext.Provider value={value}>
      {children}
    </DashboardFilterContext.Provider>
  );
};

export function useDashboardFilter(): DashboardFilterContextValue {
  const ctx = useContext(DashboardFilterContext);
  if (!ctx) {
    throw new Error(
      "useDashboardFilter must be used inside <DashboardFilterProvider>",
    );
  }
  return ctx;
}